<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification'
import { useSettingsStore } from '@/store/settings';
import { useDisplay } from 'vuetify/lib/framework.mjs'

const props = defineProps({
  showUserModeDialog: { type: Boolean, required: false, default: undefined },
});
const emit = defineEmits(['setShowUserModeDialog']);

const settingsStore = useSettingsStore();
const toast = useToast();
const { t } = useI18n();
const { mobile, smAndDown } = useDisplay();

const showUserModeDialog = computed({
  get: () => props.showUserModeDialog,
  set: value => emit('setShowUserModeDialog', value),
});

const showMore = ref(false);

const availableUserModes = [
  { id: 'basic', title: t('settings.user_mode_basic'), icon: 'mdi-account' },
  { id: 'advanced', title: t('settings.user_mode_advanced'), icon: 'mdi-doctor' },
]

const selectedUserMode = ref(
  settingsStore.userMode ? settingsStore.userMode : 'basic',
);

const advancedUserModeVideoLink = computed(() => {
  if(mobile.value) {
    return import.meta.env.VITE_MOBILE_ADVANCED_USER_MODE_URL;
  }
  return import.meta.env.VITE_DESKTOP_ADVANCED_USER_MODE_URL;
});

const basicUserModeVideoLink = computed(() => {
  if(mobile.value) {
    return import.meta.env.VITE_MOBILE_BASIC_USER_MODE_URL;
  }
  return import.meta.env.VITE_DESKTOP_BASIC_USER_MODE_URL;
});
</script>

<template>
  <v-dialog
    v-model="showUserModeDialog"
    :persistent="!settingsStore.userMode"
    max-width="1200px"
    :width="mobile ? '100%' : 'auto'"
  >
    <v-layout-card>
      <div class="d-flex flex-row justify-space-between align-center px-4">
        <div class="d-flex align-center ga-2 text-truncate">
          <v-icon icon="mdi-account-settings" />
          <v-card-title class="pa-0 flex-shrink-1 text-truncate">
            {{ $t("router_elements.user_mode") }}
          </v-card-title>
        </div>
        <v-btn
          icon="mdi-close"
          size="small"
          variant="plain"
          @click="() => (showUserModeDialog = false)"
        />
      </div>

      <v-card-text :class="{ 'px-4': smAndDown }">
        <p>
          {{ $t("settings.user_mode_text") }}
          {{ $t("settings.user_mode_text_2") }}
        </p>
        <ul class="ma-0">
          <li>
            <p class="font-weight-bold">
              {{ t('settings.user_mode_features.redirect_to_barn_management') }}
            </p>
            <p class="text-body-2">
              {{ t('settings.user_mode_features.redirect_to_barn_management_description') }}
            </p>
          </li>
        </ul>
        
        <v-list-item
          class="mt-4"
          slim
        >
          <template #title>
            <v-label>
              {{ $t('settings.user_mode_title') }}
            </v-label>
          </template>
          <v-select
            variant="outlined"
            density="comfortable"
            :list-props="{ style: 'z-index: 2410;' }"
            :items="availableUserModes"
            :model-value="selectedUserMode"
            item-text="title"
            item-value="id"
            hide-details
            @update:model-value="selectedUserMode = $event"
          >
            <template #item="{ item, props: listProps }">
              <v-list-item
                v-bind="listProps"
                :prepend-icon="item.raw.icon"
                slim
              />
            </template>
            <template #selection="{ item, props: listProps }">
              <v-list-item
                v-bind="listProps"
                class="px-2"
                :title="item.raw.title"
                slim
                density="compact"
                :lines="false"
              >
                <template #prepend>
                  <div class="mr-2">
                    <v-icon :icon="item.raw.icon" />
                  </div>
                </template>
              </v-list-item>
            </template>
          </v-select>
        </v-list-item>
      </v-card-text>

      <!-- show videos button -->
      <div class="mb-4 text-center">
        <v-btn 
          prepend-icon="mdi-arrow-down"
          append-icon="mdi-arrow-down"
          variant="plain"
          @click="showMore = !showMore"
        >
          {{ $t('general_interface.buttons.show_difference') }}
        </v-btn>
      </div>
        
      <v-expand-transition>
        <div
          v-show="showMore"
          class="mt-4 mb-6"
        >
          <v-row justify="space-evenly">
            <!-- basic user mode -->
            <v-col
              cols="12"
              md="5"
            >
              <div class="mb-2 d-flex align-center justify-center ga-2">
                <v-icon icon="mdi-account" />
                <p
                  class="font-weight-bold"
                  :class="smAndDown ? 'text-h5' : 'text-h6'"
                >
                  {{ $t('settings.user_mode_basic') }}
                </p>
              </div>
              <div class="mb-2 d-flex justify-center align-center ga-2">
                <v-icon icon="mdi-barn" />
                <v-icon icon="mdi-arrow-right-thin" />
                <v-icon icon="mdi-monitor-dashboard" />
              </div>
              <video
                loop
                playsinline
                controls
                class="w-100 rounded-lg cursor-pointer"
                style="max-height: 400px;"
              >
                <source
                  :src="basicUserModeVideoLink"
                  type="video/mp4"
                >
              </video>
            </v-col>

            <v-divider
              class="my-6"
              :vertical="!smAndDown"
              thickness="2"
            />

            <!-- advanced user mode -->
            <v-col
              cols="12"
              md="5"
            >
              <div class="mb-2 d-flex align-center justify-center ga-2">
                <v-icon icon="mdi-doctor" />
                <p
                  class="font-weight-bold"
                  :class="smAndDown ? 'text-h5' : 'text-h6'"
                >
                  {{ $t('settings.user_mode_advanced') }}
                </p>
              </div>
              <div class="mb-2 d-flex justify-center align-center ga-2">
                <v-icon icon="mdi-barn" />
                <v-icon icon="mdi-arrow-right-thin" />
                <v-icon icon="mdi-pitchfork" />
              </div>
              <video
                loop
                playsinline
                controls
                class="w-100 rounded-lg cursor-pointer"
                style="max-height: 400px;"
              >
                <source
                  :src="advancedUserModeVideoLink"
                  type="video/mp4"
                >
              </video>
            </v-col>
          </v-row>
        </div>
      </v-expand-transition>
      
      <p class="mt-4 px-4 text-center">
        {{ $t("settings.can_be_changed_in_settings") }}
      </p>

      <v-card-actions class="justify-center">
        <v-btn
          class="px-4"
          prepend-icon="mdi-check"
          variant="elevated"
          color="success"
          @click="
            () => {
              settingsStore.setUserMode(selectedUserMode);
              toast.success(t('toast.saved_user_mode', {
                mode: t(`settings.user_mode_${selectedUserMode}`),
              }))
              showUserModeDialog = false;
            }
          "
        >
          {{ $t("general_interface.buttons.save") }}
        </v-btn>
      </v-card-actions>
    </v-layout-card>
  </v-dialog>
</template>
